import React, { useState } from 'react';
import {
  Card,
  Tabs,
  Button,
  Form,
  Input,
  Select,
  Switch,
  Space,
  Tag,
  List,
  Modal,
  ColorPicker,
  TimePicker,
  message,
  Typography,
  Divider,
  Upload,
  Image,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  MailOutlined,
  SettingOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UploadOutlined,
  SendOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useReportConfig } from '../hooks/useReportConfig';
import { ReportTemplate, ReportConfig } from '../types/reportConfig';
import dayjs from 'dayjs';
import TemplatePreview from '../components/TemplatePreview';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const ConfigReportes: React.FC = () => {
  const {
    templates,
    saveTemplate,
    deleteTemplate,
    configs,
    saveConfig,
    deleteConfig,
    toggleConfigActive,
  } = useReportConfig();

  const [activeTab, setActiveTab] = useState('configs');
  const [editingTemplate, setEditingTemplate] = useState<ReportTemplate | null>(null);
  const [editingConfig, setEditingConfig] = useState<ReportConfig | null>(null);
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<ReportTemplate | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const [templateForm] = Form.useForm();
  const [configForm] = Form.useForm();

  // TEMPLATES
  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setLogoPreview(null);
    templateForm.resetFields();
    setTemplateModalVisible(true);
  };

  // Manejar upload de imagen
  const handleImageUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setLogoPreview(result);
    };
    reader.readAsDataURL(file);
    return false; // Prevenir upload automático
  };

  const handleEditTemplate = (template: ReportTemplate) => {
    setEditingTemplate(template);
    setLogoPreview(template.clientLogo || null);
    templateForm.setFieldsValue({
      ...template,
      primaryColor: template.primaryColor,
      secondaryColor: template.secondaryColor,
    });
    setTemplateModalVisible(true);
  };

  const handleSaveTemplate = async () => {
    try {
      const values = await templateForm.validateFields();
      
      const template: ReportTemplate = {
        id: editingTemplate?.id || Date.now().toString(),
        name: values.name,
        clientLogo: logoPreview || undefined,
        primaryColor: typeof values.primaryColor === 'string' 
          ? values.primaryColor 
          : values.primaryColor?.toHexString?.() || '#0088fe',
        secondaryColor: typeof values.secondaryColor === 'string'
          ? values.secondaryColor
          : values.secondaryColor?.toHexString?.() || '#1e293b',
        headerText: values.headerText,
        footerText: values.footerText,
        sections: values.sections || {
          summary: true,
          charts: true,
          topCategories: true,
          detailedList: true,
          photos: false,
          insights: true,
        },
        createdAt: editingTemplate?.createdAt || new Date().toISOString(),
      };

      saveTemplate(template);
      message.success('Template guardado correctamente');
      setTemplateModalVisible(false);
      setLogoPreview(null);
      templateForm.resetFields();
    } catch (e) {
      console.error('Error al guardar template:', e);
    }
  };

  // CONFIGS
  const handleCreateConfig = () => {
    setEditingConfig(null);
    configForm.resetFields();
    setConfigModalVisible(true);
  };

  const handleEditConfig = (config: ReportConfig) => {
    setEditingConfig(config);
    configForm.setFieldsValue({
      ...config,
      time: config.time ? dayjs(config.time, 'HH:mm') : undefined,
    });
    setConfigModalVisible(true);
  };

  const handleSaveConfig = async () => {
    try {
      const values = await configForm.validateFields();

      const config: ReportConfig = {
        id: editingConfig?.id || Date.now().toString(),
        name: values.name,
        emails: values.emails || [],
        frequency: values.frequency,
        dayOfWeek: values.dayOfWeek,
        dayOfMonth: values.dayOfMonth,
        time: values.time ? values.time.format('HH:mm') : '08:00',
        formats: values.formats || ['pdf'],
        templateId: values.templateId,
        active: values.active !== undefined ? values.active : true,
        createdAt: editingConfig?.createdAt || new Date().toISOString(),
      };

      saveConfig(config);
      message.success('Configuración guardada correctamente');
      setConfigModalVisible(false);
      configForm.resetFields();
    } catch (e) {
      console.error('Error al guardar configuración:', e);
    }
  };

  const handleDeleteConfig = (id: string) => {
    Modal.confirm({
      title: '¿Eliminar configuración?',
      content: 'Esta acción no se puede deshacer',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: () => {
        deleteConfig(id);
        message.success('Configuración eliminada');
      },
    });
  };

  const handleDeleteTemplate = (id: string) => {
    Modal.confirm({
      title: '¿Eliminar template?',
      content: 'Esta acción no se puede deshacer',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: () => {
        deleteTemplate(id);
        message.success('Template eliminado');
      },
    });
  };

  const handleSendReportNow = (config: ReportConfig) => {
    Modal.info({
      title: 'Generar y Enviar Reporte',
      content: (
        <div>
          <p>El reporte se generará con la siguiente configuración:</p>
          <ul>
            <li><strong>Destinatarios:</strong> {config.emails.join(', ')}</li>
            <li><strong>Formatos:</strong> {config.formats.join(', ').toUpperCase()}</li>
          </ul>
          <Alert
            message="Próximamente"
            description="La funcionalidad de envío automático estará disponible cuando se implemente el backend. Por ahora, el reporte se descargará y podrás enviarlo manualmente."
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </div>
      ),
      okText: 'Entendido',
    });
  };

  const handlePreviewTemplate = (template: ReportTemplate) => {
    setPreviewTemplate(template);
    setPreviewModalVisible(true);
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} style={{ color: 'white', marginBottom: 24 }}>
        Configuración de Reportes
      </Title>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'configs',
            label: (
              <span>
                <SettingOutlined /> Configuraciones
              </span>
            ),
            children: (
              <Card>
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text strong>Reportes Automáticos Configurados</Text>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleCreateConfig}
                    >
                      Nueva Configuración
                    </Button>
                  </div>

                  <List
                    dataSource={configs}
                    locale={{ emptyText: 'No hay configuraciones. Crea una nueva.' }}
                    renderItem={config => {
                      const template = templates.find(t => t.id === config.templateId);
                      return (
                        <List.Item
                          actions={[
                            <Switch
                              checked={config.active}
                              onChange={() => toggleConfigActive(config.id)}
                              checkedChildren={<CheckCircleOutlined />}
                              unCheckedChildren={<CloseCircleOutlined />}
                            />,
                            <Button
                              icon={<EditOutlined />}
                              onClick={() => handleEditConfig(config)}
                            >
                              Editar
                            </Button>,
                            <Button
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => handleDeleteConfig(config.id)}
                            >
                              Eliminar
                            </Button>,
                          ]}
                        >
                          <List.Item.Meta
                            title={
                              <Space>
                                {config.name}
                                {config.active ? (
                                  <Tag color="green">Activo</Tag>
                                ) : (
                                  <Tag>Inactivo</Tag>
                                )}
                              </Space>
                            }
                            description={
                              <Space direction="vertical" size="small">
                                <Text>
                                  <MailOutlined /> Destinatarios:{' '}
                                  {config.emails.join(', ')}
                                </Text>
                                <Text>
                                  Frecuencia:{' '}
                                  {config.frequency === 'weekly'
                                    ? 'Semanal'
                                    : config.frequency === 'monthly'
                                    ? 'Mensual'
                                    : config.frequency === 'quarterly'
                                    ? 'Trimestral'
                                    : 'Manual'}
                                </Text>
                                <Text>
                                  Template: {template?.name || 'No encontrado'}
                                </Text>
                                <Text>
                                  Formatos: {config.formats.join(', ').toUpperCase()}
                                </Text>
                                {config.lastSent && (
                                  <Text type="secondary">
                                    Último envío:{' '}
                                    {new Date(config.lastSent).toLocaleString('es-ES')}
                                  </Text>
                                )}
                              </Space>
                            }
                          />
                          <Button
                            type="primary"
                            icon={<SendOutlined />}
                            onClick={() => handleSendReportNow(config)}
                            style={{ marginTop: 8 }}
                          >
                            Enviar Ahora
                          </Button>
                        </List.Item>
                      );
                    }}
                  />
                </Space>
              </Card>
            ),
          },
          {
            key: 'templates',
            label: (
              <span>
                <FileTextOutlined /> Templates
              </span>
            ),
            children: (
              <Card>
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text strong>Templates de Reportes</Text>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleCreateTemplate}
                    >
                      Nuevo Template
                    </Button>
                  </div>

                  <List
                    grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 3, xl: 3, xxl: 4 }}
                    dataSource={templates}
                    renderItem={template => (
                      <List.Item>
                        <Card
                          title={template.name}
                          extra={
                            <Space>
                              <Button
                                size="small"
                                icon={<EyeOutlined />}
                                onClick={() => handlePreviewTemplate(template)}
                              >
                                Vista Previa
                              </Button>
                              {template.id !== 'default' && (
                                <>
                                  <Button
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => handleEditTemplate(template)}
                                  />
                                  <Button
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleDeleteTemplate(template.id)}
                                  />
                                </>
                              )}
                            </Space>
                          }
                        >
                          <Space direction="vertical" style={{ width: '100%' }}>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <div
                                style={{
                                  width: 40,
                                  height: 40,
                                  backgroundColor: template.primaryColor,
                                  borderRadius: 4,
                                }}
                              />
                              <div
                                style={{
                                  width: 40,
                                  height: 40,
                                  backgroundColor: template.secondaryColor,
                                  borderRadius: 4,
                                }}
                              />
                            </div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {template.headerText}
                            </Text>
                            <Divider style={{ margin: '8px 0' }} />
                            <Space wrap size="small">
                              {template.sections.summary && <Tag>Resumen</Tag>}
                              {template.sections.charts && <Tag>Gráficos</Tag>}
                              {template.sections.topCategories && <Tag>Categorías</Tag>}
                              {template.sections.detailedList && <Tag>Detalle</Tag>}
                              {template.sections.photos && <Tag>Fotos</Tag>}
                              {template.sections.insights && <Tag>Insights</Tag>}
                            </Space>
                          </Space>
                        </Card>
                      </List.Item>
                    )}
                  />
                </Space>
              </Card>
            ),
          },
        ]}
      />

      {/* Modal de Template */}
      <Modal
        title={editingTemplate ? 'Editar Template' : 'Nuevo Template'}
        open={templateModalVisible}
        onCancel={() => setTemplateModalVisible(false)}
        onOk={handleSaveTemplate}
        width={600}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Form form={templateForm} layout="vertical">
          <Form.Item
            name="name"
            label="Nombre del Template"
            rules={[{ required: true, message: 'Ingresa un nombre' }]}
          >
            <Input placeholder="Ej: Edificio Central - Ejecutivo" />
          </Form.Item>

          <Form.Item label="Logo del Cliente">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Alert
                message="El logo de Seprovinsa se incluye automáticamente. Aquí puedes agregar el logo del cliente."
                type="info"
                showIcon
                style={{ marginBottom: 8 }}
              />
              <Alert
                message="Tamaño recomendado: 200x80 píxeles (ancho x alto)"
                type="success"
                showIcon
                style={{ marginBottom: 8 }}
              />
              <Upload
                accept="image/*"
                beforeUpload={handleImageUpload}
                showUploadList={false}
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>Subir Logo</Button>
              </Upload>
              {logoPreview && (
                <div style={{ marginTop: 8 }}>
                  <Image
                    src={logoPreview}
                    alt="Logo preview"
                    style={{ maxWidth: 200, maxHeight: 80, objectFit: 'contain' }}
                  />
                  <Button
                    size="small"
                    danger
                    onClick={() => setLogoPreview(null)}
                    style={{ marginLeft: 8 }}
                  >
                    Eliminar
                  </Button>
                </div>
              )}
            </Space>
          </Form.Item>

          <Space style={{ width: '100%' }} size="large">
            <Form.Item name="primaryColor" label="Color Primario">
              <ColorPicker defaultValue="#0088fe" showText />
            </Form.Item>

            <Form.Item name="secondaryColor" label="Color Secundario">
              <ColorPicker defaultValue="#1e293b" showText />
            </Form.Item>
          </Space>

          <Form.Item name="headerText" label="Texto de Encabezado">
            <Input placeholder="Ej: Edificio Central - Reporte de Incidencias" />
          </Form.Item>

          <Form.Item name="footerText" label="Texto de Pie de Página">
            <TextArea rows={2} placeholder="Ej: Información de contacto, dirección, teléfono" />
          </Form.Item>

          <Divider>Secciones a Incluir</Divider>

          <Space direction="vertical">
            <Form.Item name={['sections', 'summary']} valuePropName="checked">
              <Switch /> Resumen Ejecutivo
            </Form.Item>
            <Form.Item name={['sections', 'charts']} valuePropName="checked">
              <Switch /> Gráficos
            </Form.Item>
            <Form.Item name={['sections', 'topCategories']} valuePropName="checked">
              <Switch /> Categorías Más Frecuentes
            </Form.Item>
            <Form.Item name={['sections', 'detailedList']} valuePropName="checked">
              <Switch /> Listado Detallado
            </Form.Item>
            <Form.Item name={['sections', 'photos']} valuePropName="checked">
              <Switch /> Fotos de Incidencias
            </Form.Item>
            <Form.Item name={['sections', 'insights']} valuePropName="checked">
              <Switch /> Insights Automáticos
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      {/* Modal de Configuración */}
      <Modal
        title={editingConfig ? 'Editar Configuración' : 'Nueva Configuración'}
        open={configModalVisible}
        onCancel={() => setConfigModalVisible(false)}
        onOk={handleSaveConfig}
        width={600}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Form form={configForm} layout="vertical" initialValues={{ active: true }}>
          <Form.Item
            name="name"
            label="Nombre de la Configuración"
            rules={[{ required: true, message: 'Ingresa un nombre' }]}
          >
            <Input placeholder="Ej: Reporte Mensual Edificio Central" />
          </Form.Item>

          <Form.Item
            name="emails"
            label="Emails Destinatarios"
            rules={[{ required: true, message: 'Agrega al menos un email' }]}
          >
            <Select
              mode="tags"
              placeholder="Escribe emails y presiona Enter"
              tokenSeparators={[',', ' ']}
            />
          </Form.Item>

          <Form.Item name="frequency" label="Frecuencia" initialValue="monthly">
            <Select>
              <Option value="manual">Manual</Option>
              <Option value="weekly">Semanal</Option>
              <Option value="monthly">Mensual</Option>
              <Option value="quarterly">Trimestral</Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.frequency !== currentValues.frequency
            }
          >
            {({ getFieldValue }) => {
              const frequency = getFieldValue('frequency');
              if (frequency === 'weekly') {
                return (
                  <Form.Item name="dayOfWeek" label="Día de la Semana">
                    <Select>
                      <Option value={1}>Lunes</Option>
                      <Option value={2}>Martes</Option>
                      <Option value={3}>Miércoles</Option>
                      <Option value={4}>Jueves</Option>
                      <Option value={5}>Viernes</Option>
                      <Option value={6}>Sábado</Option>
                      <Option value={0}>Domingo</Option>
                    </Select>
                  </Form.Item>
                );
              }
              if (frequency === 'monthly' || frequency === 'quarterly') {
                return (
                  <Form.Item name="dayOfMonth" label="Día del Mes">
                    <Select>
                      {Array.from({ length: 31 }, (_, i) => (
                        <Option key={i + 1} value={i + 1}>
                          {i + 1}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>

          <Form.Item name="time" label="Hora de Envío">
            <TimePicker format="HH:mm" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="formats" label="Formatos" initialValue={['pdf']}>
            <Select mode="multiple">
              <Option value="pdf">PDF</Option>
              <Option value="excel">Excel</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="templateId"
            label="Template"
            rules={[{ required: true, message: 'Selecciona un template' }]}
          >
            <Select>
              {templates.map(t => (
                <Option key={t.id} value={t.id}>
                  {t.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="active" label="Estado" valuePropName="checked">
            <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de Vista Previa */}
      <Modal
        title="Vista Previa del Template"
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        width={900}
        footer={[
          <Button key="close" onClick={() => setPreviewModalVisible(false)}>
            Cerrar
          </Button>,
        ]}
      >
        {previewTemplate && <TemplatePreview template={previewTemplate} />}
      </Modal>
    </div>
  );
};

export default ConfigReportes;
